/**
 * ClinicalTab — Batch/expiry tracking flags and storage & return policy.
 * Tab 2 of the Item form.
 *
 * Props:
 *   control   RHF control object
 *   setValue  RHF setValue — syncs drug / batch / expiry when controlled or narcotic changes
 *   watch     RHF watch — used to lock dependent toggles
 *
 * API: no direct API calls — uses react-hook-form
 *
 * Contract (inventory-master):
 *   - controlled/narcotic ⇒ is_drug, is_batch_tracked, is_expiry_tracked must be true
 *
 * TODO for backend:
 *   - return_behavior field needs PATCH /inventory/items/ support
 *   - shelf_life_days  field needs PATCH /inventory/items/ support
 */
import React from 'react';
import { Grid, Switch, FormControlLabel, Box, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import SectionCard from '../SectionCard';
import { InvTextField, InvSelect } from '../InvField';
import { RETURN_BEHAVIORS } from '../../constants/itemClassification';
import { IPD_COLORS } from '../../theme';

const TRACKING_TOGGLES = [
  {
    name: 'is_controlled_drug',
    label: 'Controlled drug',
    hint: 'Requires drug flag plus batch and expiry tracking',
  },
  {
    name: 'is_drug',
    label: 'Drug item',
    hint: 'Marks this item as a pharmacy drug — source of truth (not category)',
  },
  {
    name: 'is_narcotic',
    label: 'Narcotic',
    hint: 'Narcotic or scheduled substance — requires drug, batch, and expiry flags',
  },
  {
    name: 'is_batch_tracked',
    label: 'Batch required',
    hint: 'Batch number required on GRN, issue, and dispense',
  },
  {
    name: 'is_expiry_tracked',
    label: 'Expiry required',
    hint: 'Expiry date required on stock movements',
  },
  {
    name: 'is_high_value',
    label: 'High value item',
    hint: 'Extra approval or audit for high-value stock',
  },
];

function ClinicalToggle({ name, label, hint, control, disabled, onAfterChange }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Switch
              checked={!!field.value}
              disabled={disabled}
              onChange={e => {
                const checked = e.target.checked;
                field.onChange(checked);
                onAfterChange?.(checked);
              }}
              color="success"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: IPD_COLORS.textPrimary }}>
                {label}
              </Typography>
              {hint && (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.25, lineHeight: 1.4 }}>
                  {hint}
                </Typography>
              )}
            </Box>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      )}
    />
  );
}

export default function ClinicalTab({ control, setValue, watch }) {
  const isControlled = !!watch?.('is_controlled_drug');
  const isNarcotic = !!watch?.('is_narcotic');
  const locksDrugTracking = isControlled || isNarcotic;

  /** Controlled/narcotic require is_drug + batch + expiry (server contract). */
  const enforceControlledOrNarcotic = (checked) => {
    if (!checked) return;
    setValue('is_drug', true, { shouldDirty: true });
    setValue('is_batch_tracked', true, { shouldDirty: true });
    setValue('is_expiry_tracked', true, { shouldDirty: true });
  };

  function afterChangeFor(name) {
    if (name === 'is_controlled_drug' || name === 'is_narcotic') {
      return enforceControlledOrNarcotic;
    }
    return undefined;
  }

  function isToggleDisabled(name) {
    if (!locksDrugTracking) return false;
    return name === 'is_drug' || name === 'is_batch_tracked' || name === 'is_expiry_tracked';
  }

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <SectionCard
          title="Tracking Flags"
          subtitle="Control how this item is tracked in pharmacy and store operations."
        >
          <Grid container spacing={2}>
            {TRACKING_TOGGLES.map(f => (
              <Grid item xs={12} sm={6} key={f.name}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: `1px solid ${IPD_COLORS.border}`,
                    backgroundColor: IPD_COLORS.cardBg,
                    height: '100%',
                    opacity: isToggleDisabled(f.name) ? 0.85 : 1,
                  }}
                >
                  <ClinicalToggle
                    name={f.name}
                    label={f.label}
                    hint={
                      isToggleDisabled(f.name)
                        ? `${f.hint} (locked while controlled/narcotic is on)`
                        : f.hint
                    }
                    control={control}
                    disabled={isToggleDisabled(f.name)}
                    onAfterChange={afterChangeFor(f.name)}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      </Grid>

      {/* TODO: return_behavior and shelf_life_days need backend PATCH /inventory/items/ support */}
      <Grid item xs={12}>
        <SectionCard title="Storage & Return">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="return_behavior"
                control={control}
                render={({ field }) => (
                  <InvSelect
                    label="Return Behaviour"
                    value={field.value}
                    onChange={field.onChange}
                    options={RETURN_BEHAVIORS}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="shelf_life_days"
                control={control}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Shelf Life (days)"
                    type="number"
                    inputProps={{ min: 0 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>
    </Grid>
  );
}
